// What to show when the assessment indicates no echoic targets, quoted from the
// EESAPP Guide (Esch, 2nd edition, 2024). Kept in one place so the guided page,
// the work packet and both PDFs say exactly the same thing.

// A group is "high" at or above this percentage of 1-point items. Same threshold
// the Starting Targets Decision Box uses to pick the active group.
export const HIGH_SCORE_THRESHOLD = 80;

/**
 * True when every group score is high, i.e. no echoic targets are indicated.
 * @param {Object} groupScoresPct {1: pct, ... 5: pct}
 */
export function noTargetsIndicated(groupScoresPct) {
    if (!groupScoresPct) return false;

    return [1, 2, 3, 4, 5].every(
        g => Number(groupScoresPct[g] ?? 0) >= HIGH_SCORE_THRESHOLD
    );
}

export const NO_TARGETS_HEADING = 'No echoic targets indicated';

export const NO_TARGETS_INTRO =
    'All five group scores are high, so specific intervention for echoic skills may not be needed. The Guide says a learner who can already easily repeat multisyllabic phrases with accurate vowel sounds, mostly accurate consonants and without omitting syllables can simply be given full credit for the EESA-R.';

export const NO_TARGETS_INTRO_REF = 'EESAPP Guide, 2nd ed., p.25; FAQ Q11, p.107';

export const NO_TARGETS_ACTIVITIES_LEAD =
    'Instead of shaping targets, keep longer syllable strings fluent with the Guide’s activities:';

export const NO_TARGETS_ACTIVITIES = [
    { label: 'Sing songs', detail: '' },
    { label: 'Recite fun rhymes', detail: 'Repeat, or say in unison' },
    { label: 'Sing-song recite word families', detail: 'ban, can, dan, fan, man, ran' },
    {
        label: 'Toggle mastered 3-syllable combos with easy 4- or 5-syllable combos',
        detail: 'See Echoic Phrases (Appendix A) for sample combos',
    },
];

export const NO_TARGETS_ACTIVITIES_REF = 'EESAPP Guide, 2nd ed., p.43';

export const NO_TARGETS_CLOSING =
    'During teaching, lower-number syllable combos with less complexity can be toggled with higher-number syllable combos. These should gradually increase in syllable complexity as the combo length increases.';

// Flat lines, for the PDFs that draw text rather than render markup.
export function noTargetsPdfLines() {
    return [
        `${NO_TARGETS_INTRO} (${NO_TARGETS_INTRO_REF})`,
        NO_TARGETS_ACTIVITIES_LEAD,
        ...NO_TARGETS_ACTIVITIES.map(
            a => (a.detail ? `• ${a.label} — ${a.detail}` : `• ${a.label}`)
        ),
        `${NO_TARGETS_CLOSING} (${NO_TARGETS_ACTIVITIES_REF})`,
    ];
}
